import type {
  GetServerSideProps,
  GetStaticProps,
  NextComponentType,
  NextPageContext,
} from "next";
import type App from "next/app";
import type { AppContext } from "next/app";
import { createContext, useContext, useMemo } from "react";
import createApi from "./api";
import createAuth from "./auth";
import createCart from "./cart";
import createStorage from "./storage";
import createStrapi from "./strapi";
import type {
  AppCallback,
  Callback,
  GetInitialAppProps,
  GetInitialPageProps,
  GetServerSidePropsCallback,
  GetStaticPropsCallback,
  PageCallback,
  TLoginErrorHandler,
  TUser,
  TUtils,
  WrapperProps,
} from "./types";
import type { IncomingMessage } from "http";

const isServer = () => typeof window === "undefined";

const UtilContext = createContext<TUtils>({} as unknown as TUtils);

export const useUtils = () => {
  return useContext(UtilContext);
};

const generateUtils = (
  ctx?: NextPageContext,
  user?: TUser,
): Required<TUtils> => {
  const storage = createStorage(ctx);
  const auth = createAuth(storage, { ctx, user });
  const api = createApi({ ctx, auth });
  const strapi = createStrapi();
  const cart = createCart(api);

  return {
    auth,
    api,
    storage,
    strapi,
    cart,
  };
};

let sharedUtils: Required<TUtils> | null = null;

export const initUtils = (
  _handleLoginError: TLoginErrorHandler,
  context?: NextPageContext | AppContext,
  user?: TUser,
): Required<TUtils> => {
  if (isServer()) {
    const req:
      | (IncomingMessage & {
          __montaUtils?: Required<TUtils>;
        })
      | undefined =
      (context as NextPageContext)?.req || (context as AppContext)?.ctx?.req;
    const ctx = context && "ctx" in context ? context.ctx : context;
    if (req) {
      // ATTENTION! THIS IS INTERNAL, DO NOT ACCESS DIRECTLY ANYWHERE ELSE
      if (!req.__montaUtils) req.__montaUtils = generateUtils(ctx as any, user);
      return req.__montaUtils;
    }
    return generateUtils(ctx as any, user);
  }

  if (!sharedUtils) {
    sharedUtils = generateUtils(undefined, user);
  }
  return sharedUtils;
};

const makeProps = async ({
  callback,
  context,
  addUtilsToContext = false,
}: {
  callback: Callback<Required<TUtils>, any>;
  context: any;
  addUtilsToContext?: boolean;
}): Promise<WrapperProps> => {
  const utils = initUtils(context);

  if (addUtilsToContext) {
    if (context.ctx) {
      context.ctx.utils = utils;
    } else {
      context.utils = utils;
    }
  }

  const nextCallback = callback && callback(utils);
  const initialProps = (nextCallback && (await nextCallback(context))) || {};

  return { initialProps };
};

export const useWrappedUtils = (
  { initialProps, ...props }: any,
  handleLoginError: TLoginErrorHandler,
): { utils: TUtils; props: any } => {
  const initialStateFromGSPorGSSR = props?.pageProps?.initialState;

  let resultProps: any = props;

  if (initialProps && initialProps.pageProps) {
    resultProps.pageProps = {
      ...initialProps.pageProps, // this comes from wrapper in _app mode
      ...props.pageProps, // this comes from gssp/gsp in _app mode
    };
  }

  if (initialStateFromGSPorGSSR) {
    resultProps = { ...props, pageProps: { ...props.pageProps } };
    delete resultProps.pageProps.initialState;
  }

  // unwrap getInitialPageProps
  if (resultProps?.pageProps?.initialProps) {
    resultProps.pageProps = {
      ...resultProps.pageProps,
      ...resultProps.pageProps.initialProps,
    };
    delete resultProps.pageProps.initialProps;
  }

  const utils = useMemo<TUtils>(
    () => initUtils(handleLoginError, undefined, resultProps?.pageProps?.user),
    [],
  );

  return { utils, props: { ...initialProps, ...resultProps } };
};

export const withUtils = (
  Component: NextComponentType | App | any,
  handleLoginError: TLoginErrorHandler,
) => {
  const WrappedComponent = (props: any) => {
    const { utils, props: combinedProps } = useWrappedUtils(
      props,
      handleLoginError,
    );

    combinedProps.pageProps;

    return (
      <UtilContext.Provider value={utils}>
        <Component {...combinedProps} />
      </UtilContext.Provider>
    );
  };

  WrappedComponent.displayName = `withUtils(${
    Component.displayName || Component.name || "Component"
  })`;

  if ("getInitialProps" in Component) {
    WrappedComponent.getInitialProps = Component.getInitialProps;
  }

  return WrappedComponent;
};

export const getInitialPageProps =
  <P extends Record<string, unknown> = any>(
    callback: PageCallback<TUtils, P>,
  ): GetInitialPageProps<P> =>
  async (context: NextPageContext | any) => {
    if ("getState" in context) {
      return callback && callback(context as any);
    }
    return makeProps({ callback, context, addUtilsToContext: true });
  };

export const getInitialAppProps =
  <P extends Record<string, unknown> = any>(
    callback: AppCallback<TUtils, P>,
  ): GetInitialAppProps<P> =>
  async (context: AppContext) => {
    const { initialProps, initialState } = await makeProps({
      callback,
      context,
      addUtilsToContext: true,
    });
    return {
      ...initialProps,
      initialState,
    };
  };

export const getStaticProps =
  <P extends Record<string, unknown> = any>(
    callback: GetStaticPropsCallback<Required<TUtils>, P>,
  ): GetStaticProps<P> =>
  async (context) => {
    const { initialProps } = await makeProps({
      callback,
      context,
    });
    return {
      ...initialProps,
      props: {
        ...initialProps.props,
      },
    } as any;
  };

export const getServerSideProps =
  <P extends Record<string, unknown> = any>(
    callback: GetServerSidePropsCallback<TUtils, P>,
  ): GetServerSideProps<P> =>
  async (context) =>
    await getStaticProps(callback as any)(context);

export const wrapper = {
  getInitialAppProps,
  getInitialPageProps,
  getServerSideProps,
  getStaticProps,
  useUtils,
  withUtils,
  useWrappedUtils,
};
